import { LocaleSwitcher } from '@/i18n/LocaleSwitcher';
import { useCaptureStore } from '@/store/captureStore';
import { useBrandedDocumentTitle } from '@/theme/documentTitle';
import { type TenantConfig, brandTokensFor, tenantFromSummary } from '@/theme/tenant';
import { Dataroom } from '@vcip/design-system';
import type { CSSProperties, ReactNode } from 'react';
import { DemoSheet } from '../demos/DemoSheet';
import { HelpButton } from '../help/HelpButton';
import { HelpProvider } from '../help/HelpContext';
import { HelpSheet } from '../help/HelpSheet';
import { PoweredByKycHub } from './PoweredByKycHub';

/**
 * The one screen shell: design-system `<Dataroom>` (the `.droom` consumer ramp + `--brand*` hook),
 * the phone-width `.app-screen`, the platform credit and the locale switcher. Every route renders
 * through here so no screen can ship without the credit or the switcher (the droom's lesson).
 *
 * DEC-092: this IS the app root the `--brand*` CSS variables are written on. A screen that passes no
 * tenant (loading, an entry error) still reskins from the moment the tenant is known — the store's
 * exchange summary is the fallback — and falls back to the neutral platform theme when it is not.
 *
 * SPEC_CAPTURE_UX_R2 §7.1: the help layer lives here for the same reason — the persistent help button and the
 * step help sheet exist on EVERY screen, error states included; a screen registers its step with `useHelpStep`.
 *
 * `camera` (FSD-20 Addendum C): the capture screens. Their ONE bottom bar (`CaptureBar`) carries the compact switcher
 * and the credit itself, so the screen spends no height on a second language row under the camera. The credit and
 * the switcher still exist on every screen; on these two they live in the bar. `.is-camera` also lets a phone held
 * sideways give the camera the whole screen (no `:has()` — the iOS 14.3 floor).
 */
export function Shell({ tenant, camera = false, children }: { tenant: TenantConfig | null; camera?: boolean; children: ReactNode }): JSX.Element {
  const served = useCaptureStore((s) => s.summary);
  const effective = tenant ?? tenantFromSummary(served);
  useBrandedDocumentTitle(effective?.name);
  const style = effective ? brandTokensFor(effective) : undefined;
  return (
    <Dataroom
      {...(effective ? { brand: effective.id } : {})}
      {...(style ? { style: style as CSSProperties } : {})}
      data-branded={effective?.branding.brandColor ? 'true' : 'false'}
    >
      <HelpProvider>
        <main className={camera ? 'app-screen is-camera' : 'app-screen'}>
          <HelpButton />
          {children}
          {camera ? null : (
            <>
              <LocaleSwitcher />
              <PoweredByKycHub />
            </>
          )}
          <HelpSheet />
          {/* SPEC_CAPTURE_UX_R2 §4: the demonstration sheet is mounted ONCE here — the capture screens ask for it on
              a first encounter, the help sheet's "Show me" re-opens it, and no screen owns a second copy. */}
          <DemoSheet />
        </main>
      </HelpProvider>
    </Dataroom>
  );
}

/** A centred message screen (entry errors, not-found, stopped). */
export function CentredNotice({ title, body, action }: { title: string; body: string; action?: ReactNode }): JSX.Element {
  return (
    <div className="app-view center">
      <div className="center-col" style={{ maxWidth: '34ch' }}>
        <div className="h-title sm">{title}</div>
        <p className="sub" style={{ marginTop: 8 }}>
          {body}
        </p>
        {action ? <div style={{ marginTop: 18 }}>{action}</div> : null}
      </div>
    </div>
  );
}
